import {
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  signal,
  ViewChild
} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {OrderComponent} from '../../order/order.component';
import {CategoryStore} from '../../store/category.store';
import {PromosStore} from '../../store/promos.store';
import {ConfigurationStore} from '../../store/configuration.store';
import {TypeOrderEnum} from '../../enum/type-order.enum';
import {ThemeService} from '../../services/theme.service';
import {isPlatformBrowser, NgClass, NgOptimizedImage, NgStyle} from '@angular/common';
import {Router, RouterLink} from '@angular/router';
import {MatButtonModule} from '@angular/material/button';
import {MatIconModule} from '@angular/material/icon';
import {MatCardModule} from '@angular/material/card';
import {FormsModule} from '@angular/forms';
import {MatCheckboxModule} from '@angular/material/checkbox';
import {TenantService} from '../../services/tenant.service';

@Component({
  selector: 'app-category',
  standalone: true,
  templateUrl: './category.component.html',
  styleUrl: './category.component.scss',
  imports: [
    MatButtonModule,
    MatIconModule,
    MatCardModule,
    FormsModule,
    MatCheckboxModule,
    NgClass,
    NgStyle,
    NgOptimizedImage,
    RouterLink
  ],

})
export class CategoryComponent implements OnInit, OnDestroy {
  private platformId = inject(PLATFORM_ID)
  private categoryStore = inject(CategoryStore);
  promosStore = inject(PromosStore);
  private themeService = inject(ThemeService);
  data = this.categoryStore.selectCategories();
  dataPromo = this.promosStore.selectPromotions(['product-offer', 'order-offer']);
  private router = inject(Router);
  FORTY_DAYS_MS = 40 * 24 * 60 * 60 * 1000;
  private now = signal(Date.now());
  sortedData = computed(() => {
    const items = this.dataPromo() ?? [];
    const now = this.now();

    const filtered = items.filter((item: any) => {
      if (!item.activatesAt || !item.expiresAt) return true;

      const activatesAtTime = new Date(item.activatesAt).getTime();
      const expiresAtTime = new Date(item.expiresAt).getTime();

      const isCurrentlyActive = now >= activatesAtTime && now <= expiresAtTime;

      const isUpcomingWithin40Days = activatesAtTime > now && activatesAtTime <= now + this.FORTY_DAYS_MS;

      return isCurrentlyActive || isUpcomingWithin40Days;
    });
    const enriched = filtered.map((item: any) => ({
      ...item,
      _isAvailable: this.computeIsAvailable(item, now),
      _availabilityLabel: this.computeAvailabilityLabel(item, now),
    }));

    return [...enriched].sort(
      (a: any, b: any) =>
        (a.type === 'product-offer' ? 0 : 1) -
        (b.type === 'product-offer' ? 0 : 1)
    );
  });

  dataPromos = this.promosStore.selectPromotions(['product-discount', 'order-discount']);
  canScrollLeft = false;
  canScrollRight = true;
  private readonly configStore = inject(ConfigurationStore);
  readonly phones = this.configStore.phones;
  hasOverflow = false;
  showSeeMore = false;
  marqueeItems = [
    'NOUVEAUTÉ DU MOMENT',
    'CRISPY DEALS'
  ];
  @ViewChild('scrollContainer') scrollContainer!: ElementRef<HTMLDivElement>;

  private resizeObserver: ResizeObserver | null = null;
  private isScrollListenerAttached = false;
  private tenantService = inject(TenantService);
  isMilano = false;

  constructor(private dialog: MatDialog,
              private cdr: ChangeDetectorRef) {
    if (isPlatformBrowser(this.platformId)) {
      setInterval(() => this.now.set(Date.now()), 60_000);
    }
    effect(() => {
      const promos = this.dataPromo()
      if (promos && promos.length > 0) {
        queueMicrotask(() => {
          this.setupElementListeners()
        })
      }
    })
  }

  ngOnInit() {
    this.isMilano = this.tenantService.getActiveTenant() === 'milano';

  }

  private computeIsAvailable(card: any, now: number): boolean {
    if (!card.activatesAt) return true;
    return new Date(card.activatesAt).getTime() <= now;
  }

  private computeAvailabilityLabel(card: any, now: number): string | null {
    if (!card.activatesAt || this.computeIsAvailable(card, now)) return null;

    const activatesAtDate = new Date(card.activatesAt);
    const nowDate = new Date(now);

    const isSameDay =
      activatesAtDate.getFullYear() === nowDate.getFullYear() &&
      activatesAtDate.getMonth() === nowDate.getMonth() &&
      activatesAtDate.getDate() === nowDate.getDate();

    if (isSameDay) {
      const time = activatesAtDate.toLocaleTimeString('fr-FR', {
        hour: '2-digit',
        minute: '2-digit'
      });
      return `Disponible à ${time}`;
    }

    const formatted = activatesAtDate.toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long'
    });
    return `Disponible le ${formatted}`;
  }

  isPromoAvailable(card: any): boolean {
    if (!card.activatesAt) return true;
    return new Date(card.activatesAt).getTime() <= Date.now();
  }

  ngOnDestroy(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  isOrderOffer(card: any): boolean {
    return card.type === 'order-offer';
  }

  checkOverflow() {
    if (!this.scrollContainer?.nativeElement) return;

    const el = this.scrollContainer.nativeElement;
    const tolerance = 5;
    const needScroll = el.scrollWidth > el.clientWidth + tolerance;

    this.hasOverflow = needScroll;
    this.showSeeMore = needScroll;
    this.updateArrowVisibility();

    this.cdr.markForCheck()
  }

  updateArrowVisibility() {
    if (!this.scrollContainer?.nativeElement) return;

    const el = this.scrollContainer.nativeElement;

    // Marge de 10px pour éviter les bugs de précision
    this.canScrollLeft = el.scrollLeft > 10;
    this.canScrollRight = el.scrollLeft + el.clientWidth < el.scrollWidth - 10;
    this.cdr.markForCheck()
  }

  private setupElementListeners() {
    if (!isPlatformBrowser(this.platformId)) return;
    if (!this.scrollContainer?.nativeElement) return;
    const el = this.scrollContainer.nativeElement;

    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    }

    this.resizeObserver = new ResizeObserver(() => {
      this.checkOverflow()
    })
    this.resizeObserver.observe(el)

    if (!this.isScrollListenerAttached) {
      el.addEventListener('scroll', () => this.updateArrowVisibility(), {passive: true})
      this.isScrollListenerAttached = true;
    }

    this.checkOverflow()
  }

  scrollRight() {
    this.scrollContainer.nativeElement.scrollBy({left: 300, behavior: 'smooth'});
  }

  scrollLeft() {
    this.scrollContainer.nativeElement.scrollBy({left: -300, behavior: 'smooth'});
  }

  cardClasses = [
    'bg-gradient-orange',
    'bg-gradient-yellow',
    'bg-gradient-orange-brown',
    'bg-gradient-peach'
  ];

  getCardClass(index: number): string {
    return this.cardClasses[index % this.cardClasses.length];
  }


  getPrimaryBadge(activatedFor: string[]): { label: string; class: string } | null {
    if (!activatedFor || activatedFor.length === 0) {
      return null;
    }

    // Vérifier si tous les types sont présents
    const hasAllClients = activatedFor.includes('ALL_CLIENTS');
    const hasExisting = activatedFor.includes('EXISTING_CLIENT');
    const hasNew = activatedFor.includes('NEW_CLIENT');

    // Si tous les types sont présents ou si c'est "ALL_CLIENTS" seul
    if (hasAllClients) {
      // Vérifier si c'est vraiment "tous les clients"
      // (soit ALL_CLIENTS seul, soit tous les 3 types)
      const isAllClients = (hasAllClients && !hasExisting && !hasNew) ||
        (hasAllClients && hasExisting && hasNew);

      if (isAllClients) {
        return null;

      }
    }

    // Sinon, priorité normale
    if (hasNew) {
      return {
        label: 'Nouveau client',
        class: 'bg-orange-500 text-white animate-pulse'
      };
    }

    if (hasExisting) {
      return {
        label: 'Client existant',
        class: 'bg-orange-500 text-white animate-pulse'
      };
    }

    // Fallback
    return {
      label: activatedFor[0],
      class: 'bg-orange-500 text-white animate-pulse'
    };
  }


  trackById(item: any): string {
    return item.id;
  }

  openDialog(categoryId?: string, categorySlug?: string) {
    this.router.navigate(['/product/category',categorySlug], {
      state: {
        selectedCategoryId: categoryId,
        selectedCategorySlug: categorySlug
      }
    });
  }

  // openDialog(categoryId?: string, categorySlug?: string) {
  //   this.dialog.open(OrderComponent, {
  //     panelClass: 'custom-dialog',
  //     autoFocus: false,
  //     disableClose: true,
  //     data: { selectedCategoryId: categoryId, selectedCategorySlug: categorySlug }
  //   });
  // }

  openDialogForPromo(card: any) {
    const categoryId = card.categoryId || undefined;

    const orderTypes = card.orderTypes || [];
    const typedOrderTypes = this.convertToTypeOrderEnum(orderTypes);

    const firstOrderType = typedOrderTypes.length > 0 ? typedOrderTypes[0] : undefined;

    if (card.type === 'order-offer') {
      this.openDialogOrder(categoryId, firstOrderType, typedOrderTypes, 'order', card.slug);
      return;
    }

    if (card.type === 'product-offer' && typedOrderTypes?.length) {
      this.openDialogOrder(
        categoryId,
        firstOrderType,
        typedOrderTypes,
        'promo',
        card.slug,
        card.id
      );
      return;
    }

    this.openDialogOrder(categoryId, firstOrderType, typedOrderTypes);
  }

  private convertToTypeOrderEnum(orderTypes: string[]): TypeOrderEnum[] {
    const typeMap: { [key: string]: TypeOrderEnum } = {
      'À Emporter': TypeOrderEnum.TAKEAWAY,
      'En Livraison': TypeOrderEnum.DELIVERY,
      'Sur Place': TypeOrderEnum.ON_SITE,
    };

    return orderTypes
      .map(type => typeMap[type])
      .filter(Boolean);
  }

  openDialogOrder(
    categoryId?: string,
    orderType?: TypeOrderEnum,
    allowedOrderTypes?: TypeOrderEnum[],
    context?: 'promo' | 'order',
    promoSlug?: string,
    promoId?: string
  ) {
    this.dialog.open(OrderComponent, {
      panelClass: 'custom-dialog',
      autoFocus: false,
      disableClose: true,
      data: {
        selectedCategoryId: categoryId,
        preselectedOrderType: orderType,
        allowedOrderTypes,
        context,
        promoSlug,
        promoId
      }
    });
  }

  getRepeatArray(items: any[] | null): number[] {
    if (!items || items.length === 0) return [0, 1, 2, 3];

    const minTotal = 10;
    const repeatCount = Math.max(2, Math.ceil(minTotal / items.length));

    return Array.from({length: repeatCount * 2}, (_, i) => i);
  }
}
